import { useMemo } from "react";
import toast from "react-hot-toast";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { HtmlTextFormatter } from "../../Utils";
import { PMField } from "../../utils/PMField";
import { QuillEditor } from "../../utils/quill-editor";
import { FormikSaveBar } from "../../utils/FormikSaveBar";
import { useCompanyHasModule } from "../../../routes/roleOnly";
import { PMNotice } from "../../utils/notifications";
import { DocumentCenter } from "../documents/document-center";
import {
  CubeIcon,
  ClockIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import PMSwitch from "../../utils/PMSwitch";
import { updateDeviation } from "../../../api/quality/deviation";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { DeliveryAddressSelector } from "../../utils/moduleSelectors/deliveryAddressSelector";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { customerLockBlocksSubmit } from "../../../contexts/CustomerLockTracker";
import ParticipantsSection from "../projects/participants";
import { WorkOrderAccessesSection } from "./WorkOrderAccessesSection";
import { WorkOrderPlanSection } from "./work-order-plan-section";
import { workOrderParticipantsRole } from "./utils";
import { ModernProjectActivitySelectorField } from "../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { createProjectActivity } from "../../../api/project_activity";
import { TimeRegistrationSection } from "../hours/time-registration-section";
import { WorkOrderChecklistPanel } from "./work-order-checklist-panel";
import { getHoursAsync } from "../../../slices/hour";

/** Forslag til AO-tittel fra avvik (nummer + tittel) når ordre opprettes fra avvik. */
export function formatSuggestedWorkOrderTitleFromDeviation(hint, t) {
  if (!hint || typeof hint !== "object") return "";
  const label = (hint.displayTitle || hint.title || "").trim();
  const num = hint.number;
  const numStr =
    num != null && String(num).trim() !== "" ? String(num).trim() : "";
  if (numStr && label) {
    return t("deviation.work_order.suggested_wo_title_full", {
      number: numStr,
      title: label,
    });
  }
  if (label) return label;
  if (numStr) {
    return t("deviation.work_order.suggested_wo_title_number_only", {
      number: numStr,
    });
  }
  return "";
}

/** Pakker ut en enkelt post fra API (støtter { data: {...} } og dobbelt innpakket). */
export function pickJsonRecord(body) {
  if (body == null || typeof body !== "object" || Array.isArray(body)) return null;
  const d = body.data;
  if (d != null && typeof d === "object" && !Array.isArray(d)) {
    if (
      "data" in d &&
      d.data != null &&
      typeof d.data === "object" &&
      !Array.isArray(d.data)
    ) {
      return d.data;
    }
    return d;
  }
  return body;
}

export function deviationLinkFailureMessage(linkRes, t) {
  if (linkRes?.isAxiosError) {
    const errData = linkRes.response?.data;
    const errs = errData?.errors;
    if (Array.isArray(errs) && errs.length) return String(errs[0]);
    if (typeof errs === "string" && errs.trim()) return errs.trim();
    if (errs && typeof errs === "object" && !Array.isArray(errs)) {
      const flat = Object.values(errs).flat();
      if (flat.length) return String(flat[0]);
    }
    if (typeof errData?.error === "string") return errData.error;
  }
  return t("deviation.work_order.link_failed_after_create");
}

/** Fallback: AO-tittel fra prosjekt (nummer · visningsnavn) når det ikke finnes avvik-hint. */
export function suggestedWorkOrderTitleFromProject(project) {
  if (!project) return "";
  const label = (project.displayTitle || project.name || "").trim();
  const num = project.project_number;
  const numStr = num != null && String(num).trim() !== "" ? String(num).trim() : "";
  if (label && numStr) {
    return `${numStr} · ${label}`;
  }
  return label || numStr;
}

/**
 * Editerbart AO-skjema. Brukes inline på arbeidsordresiden (readWorkOrder) for
 * både opprettelse (ingen id) og redigering (eksisterende id). Må ligge utenfor
 * forelder-komponenten — ellers remountes Formik ved hver parent state-oppdatering
 * (f.eks. når tittel kopieres).
 */
export function WorkOrderEditForm({
  initialValues,
  setInitialValues,
  errors,
  setErrors,
  work_order,
  setWorkOrder,
  project,
  setProject,
  customer,
  setCustomer,
  delivery_address,
  setDeliveryAddress,
  canMutateWorkOrderFields,
  canEditWorkOrderUi,
  workOrderActivityListParams,
  createOrUpdate,
  navigate,
  t,
  returnAfterWorkOrderCreate = null,
  handleParticipantsChange,
  canAccessProjectPlan,
  costEstimationOpen,
  setCostEstimationOpen,
  newProjectActivityTitle,
  setNewProjectActivityTitle,
  creatingProjectActivity,
  setCreatingProjectActivity,
  openRegistrationDialogSignal,
  permissions,
  onCancelEdit = null,
  /** When true the form is embedded in the right-side edit drawer: it does not
   *  navigate after saving an existing order and renders an inline footer. */
  drawerMode = false,
  /** Called with the saved record after updating an existing order in drawer mode. */
  onSaved = null,
}) {
  const qualityChecklistsModule = useCompanyHasModule("quality_checklists");
  const dispatch = useDispatch();
  const hoursController = useMemo(
    () => ({
      query: "",
      page: 1,
      per_page: 100,
      sortBy: "created_at",
      sort: "desc",
      filters:
        work_order?.id ? [{ name: "WORK_ORDER", value: work_order.id }] : [],
      view: null,
      fields: "time,effective_hourly_rate",
    }),
    [work_order?.id]
  );

  return (
    <div
      className={
        drawerMode
          ? ""
          : "mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 pb-24 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3 lg:pb-0"
      }
    >
      <div
        className={
          drawerMode
            ? "space-y-4"
            : "space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3"
        }
      >
        <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={Yup.object({
              description: Yup.string(),
            })}
            onSubmit={async (values, helpers) => {
              try {
                const { submit, project_activity, ...rest } = values;
                if (work_order?.id) {
                  delete rest.participants;
                }
                const payload = {
                  ...rest,
                  project_activity_id:
                    rest.project_activity_id === "" ||
                    rest.project_activity_id == null
                      ? null
                      : Number(rest.project_activity_id) ||
                        rest.project_activity_id,
                };
                const response = await createOrUpdate(payload);

                const workOrderData = pickJsonRecord(response?.data ?? response);

                if (workOrderData?.id) {
                  setWorkOrder(workOrderData);
                  setInitialValues((prev) => ({
                    ...prev,
                    ...values,
                    id: workOrderData.id,
                    project_activity_id:
                      workOrderData.project_activity_id ??
                      payload.project_activity_id,
                    project_activity:
                      workOrderData.project_activity ??
                      values.project_activity,
                  }));
                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);

                  const isFirstSave = !work_order?.id;
                  const deviationReturnId =
                    returnAfterWorkOrderCreate?.deviation_id;

                  if (isFirstSave && deviationReturnId) {
                    const linkRes = await updateDeviation({
                      id: deviationReturnId,
                      work_order_id: workOrderData.id,
                    });

                    const linkedDeviation = linkRes?.isAxiosError
                      ? null
                      : pickJsonRecord(linkRes) ?? linkRes;

                    if (linkedDeviation?.id) {
                      toast.success(
                        t("deviation.work_order.linked_after_create")
                      );
                      navigate(
                        `/dashboard/hse-ks/deviations/${deviationReturnId}`
                      );
                      return;
                    }

                    toast.error(deviationLinkFailureMessage(linkRes, t));
                    navigate(`/dashboard/work_orders/${workOrderData.id}`);
                    return;
                  }

                  // In drawer mode editing an existing order, stay on the read
                  // page (the drawer just closes) instead of navigating.
                  if (!isFirstSave && (drawerMode || onSaved)) {
                    toast.success("Arbeidsordre ble lagret");
                    onSaved?.(workOrderData);
                    return;
                  }

                  toast.success("Arbeidsordre ble lagret");
                  navigate(`/dashboard/work_orders/${workOrderData.id}`);
                } else {
                  console.error(
                    "No work order data received:",
                    workOrderData
                  );
                  toast.error("No work order data received");
                  helpers.setSubmitting(false);
                }
              } catch (error) {
                console.error("Submission error:", error);
                const errorMessage =
                  error?.response?.data?.errors?.[0] ||
                  error?.message ||
                  "An error occurred while saving.";
                toast.error(errorMessage);
                setErrors([errorMessage]);
                helpers.setErrors({
                  submit: errorMessage,
                });
                helpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Form>
                {errors &&
                  errors.length > 0 &&
                  errors.map((error, index) => (
                    <PMNotice
                      severity={"error"}
                      key={index}
                      title="Error!"
                      description={error}
                    />
                  ))}

                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    {work_order?.id && onCancelEdit ? (
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          {t("work_orders.details_section_title")}
                        </h3>
                        <button
                          type="button"
                          onClick={onCancelEdit}
                          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                        >
                          <XMarkIcon className="h-4 w-4" />
                          {t("work_orders.back_to_view")}
                        </button>
                      </div>
                    ) : null}
                    <div className="grid sm:grid-cols-8 gap-6">
                      <div className="sm:col-span-8">
                        <PMField
                          name="title"
                          type="text"
                          label={t("Title")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <PMField
                          name="order_date"
                          type="date"
                          label={t("Order date")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-4">
                        <PMField
                          name="number"
                          type="text"
                          label={t("Number")}
                          disabled={true}
                          {...formik}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="shadow sm:overflow-hidden sm:rounded-md mt-5">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div className="grid sm:grid-cols-8 gap-6">
                      <div className="sm:col-span-2">
                        <ProjectSelector
                          name="project_id"
                          selected={project}
                          activeOnly="true"
                          disabled={!canMutateWorkOrderFields}
                          onChange={(e) => {
                            setProject(e?.target?.object);
                            setCustomer(e?.target?.object?.customer);
                            setDeliveryAddress(
                              e?.target?.object?.delivery_address
                            );
                            formik.setFieldValue("project_activity_id", null);
                            formik.setFieldValue("project_activity", null);
                          }}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <CustomerSelector
                          label="Kunde"
                          selected={customer}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-4">
                        <PMField
                          name="due_date"
                          type="date"
                          label={t("Due date")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <div className="py-6">
                          <PMSwitch
                            name={"is_urgent"}
                            label={t("Is Urgent")}
                            disabled={!canMutateWorkOrderFields}
                            {...formik}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3">
                        <DeliveryAddressSelector
                          label="Oppdragsadresse"
                          module="CUSTOMER"
                          module_param={customer?.id}
                          selected={delivery_address}
                          {...formik}
                        />
                      </div>

                      <div className="sm:col-span-8 border-t border-gray-100 pt-4 mt-2">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {t("work_orders.project_activity.section_title")}
                        </h4>
                        <p className="mt-1 text-xs text-gray-500">
                          {t("work_orders.project_activity.help")}
                        </p>
                        <div className="mt-3 sm:max-w-xl">
                          <ModernProjectActivitySelectorField
                            name="project_activity_id"
                            label={false}
                            selected={formik.values.project_activity}
                            project={project}
                            setFieldValue={formik.setFieldValue}
                            errors={formik.errors}
                            disabled={!canMutateWorkOrderFields}
                            listFetchParams={workOrderActivityListParams}
                          />
                          {formik.values.project_activity_id != null &&
                            formik.values.project_activity_id !== "" &&
                            canMutateWorkOrderFields && (
                              <button
                                type="button"
                                className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500"
                                onClick={() => {
                                  formik.setFieldValue(
                                    "project_activity_id",
                                    null
                                  );
                                  formik.setFieldValue(
                                    "project_activity",
                                    null
                                  );
                                }}
                              >
                                {t("project_activity.clear_selection")}
                              </button>
                            )}
                        </div>
                        {canMutateWorkOrderFields && project?.id && (
                          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:max-w-2xl">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                                <label
                                  htmlFor="new_project_activity_title"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  {t(
                                    "work_orders.project_activity.new_title_label"
                                  )}
                                </label>
                                <button
                                  type="button"
                                  disabled={
                                    creatingProjectActivity ||
                                    !String(
                                      formik.values.title || ""
                                    ).trim()
                                  }
                                  onClick={() =>
                                    setNewProjectActivityTitle(
                                      String(
                                        formik.values.title || ""
                                      ).trim()
                                    )
                                  }
                                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500 disabled:cursor-not-allowed disabled:text-gray-400"
                                >
                                  {t(
                                    "work_orders.project_activity.copy_from_title"
                                  )}
                                </button>
                              </div>
                              <input
                                id="new_project_activity_title"
                                type="text"
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                value={newProjectActivityTitle}
                                onChange={(e) =>
                                  setNewProjectActivityTitle(e.target.value)
                                }
                                placeholder={t(
                                  "work_orders.project_activity.new_title_placeholder"
                                )}
                                disabled={creatingProjectActivity}
                              />
                            </div>
                            <PMButton
                              type="button"
                              className="shrink-0 w-full sm:w-auto"
                              disabled={
                                creatingProjectActivity ||
                                !newProjectActivityTitle.trim()
                              }
                              onClick={async () => {
                                const title = newProjectActivityTitle.trim();
                                if (!title || !project?.id) return;
                                setCreatingProjectActivity(true);
                                try {
                                  const row = await createProjectActivity(
                                    project.id,
                                    { title }
                                  );
                                  if (!row?.id) {
                                    throw new Error("Invalid response");
                                  }
                                  formik.setFieldValue(
                                    "project_activity_id",
                                    row.id
                                  );
                                  formik.setFieldValue(
                                    "project_activity",
                                    row
                                  );
                                  setNewProjectActivityTitle("");
                                  toast.success(
                                    t(
                                      "work_orders.project_activity.created_toast"
                                    )
                                  );
                                } catch (err) {
                                  const msg =
                                    err?.response?.data?.errors?.[0] ||
                                    err?.message ||
                                    t(
                                      "work_orders.project_activity.create_error"
                                    );
                                  toast.error(msg);
                                } finally {
                                  setCreatingProjectActivity(false);
                                }
                              }}
                            >
                              {creatingProjectActivity
                                ? t(
                                    "work_orders.project_activity.creating"
                                  )
                                : t(
                                    "work_orders.project_activity.create_button"
                                  )}
                            </PMButton>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Budget Section */}
                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">
                          Budsjett
                        </h3>
                      </div>
                      <div className="sm:col-span-2">
                        <PMField
                          name="budgeted_hours"
                          type="number"
                          label={t("Budgeted Hours")}
                          step="0.5"
                          min="0"
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <PMSwitch
                          name="cost_included_in_project"
                          label={t("Cost included in project")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <PMSwitch
                          name="sales_income_included_in_project"
                          label={t("Sales income included in project")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <PMSwitch
                          name="do_not_expect_income"
                          label={t("Do not expect income")}
                          disabled={!canMutateWorkOrderFields}
                          {...formik}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-3">
                        Beskrivelse
                      </div>

                      <div className="col-span-6">
                        {canMutateWorkOrderFields ? (
                          <QuillEditor
                            name="description"
                            rows={20}
                            {...formik}
                          />
                        ) : work_order?.description ? (
                          <HtmlTextFormatter
                            string={work_order?.description}
                          />
                        ) : (
                          <PMNotice
                            severity={"info"}
                            title="Ingen beskrivelse"
                            description="Ingen beskrivelse er lagt til."
                          />
                        )}
                      </div>

                      <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                    </div>
                  </div>
                </div>

                {/* Cost Estimation Section */}
                <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                  <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() =>
                        setCostEstimationOpen(!costEstimationOpen)
                      }
                    >
                      <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          Kostnadsestimering
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Estimert kostnad basert på timer og vareforbruk
                        </p>
                      </div>
                      <div className="flex items-center">
                        {costEstimationOpen ? (
                          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {costEstimationOpen && (
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-6">
                          <div className="space-y-4">
                            <div className="border rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">
                                Arbeidskostnader
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Budsjetterte timer
                                  </label>
                                  <p className="text-lg font-semibold text-gray-900">
                                    {formik.values.budgeted_hours || 0} timer
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Gjennomsnittlig timepris
                                  </label>
                                  <p className="text-lg font-semibold text-gray-900">
                                    kr 500/t
                                  </p>
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Estimert arbeidskostnad
                                  </label>
                                  <p className="text-xl font-bold text-blue-600">
                                    kr{" "}
                                    {(
                                      (formik.values.budgeted_hours || 0) *
                                      500
                                    ).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="border rounded-lg p-4">
                              <h4 className="font-medium text-gray-900 mb-3">
                                Materialkostnader
                              </h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Estimert materialkostnad
                                  </label>
                                  <p className="text-lg font-semibold text-gray-900">
                                    kr 0
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    * Kan registreres via vareforbruk
                                  </p>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700">
                                    Status
                                  </label>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Estimert
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                              <h4 className="font-bold text-gray-900 mb-3">
                                Total estimert kostnad
                              </h4>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">
                                  Sum kostnader:
                                </span>
                                <span className="text-2xl font-bold text-blue-600">
                                  kr{" "}
                                  {(
                                    (formik.values.budgeted_hours || 0) * 500
                                  ).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                * Dette er et estimat basert på budsjetterte
                                timer og forventet materialkostnad
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {work_order?.id ? (
                  <WorkOrderAccessesSection
                    workOrderId={work_order.id}
                    participants={work_order.participants || []}
                    onParticipantsChange={handleParticipantsChange}
                    canManage={canEditWorkOrderUi}
                    className="mt-5"
                  />
                ) : (
                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          {t("work_order.participants.title")}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {t("work_order.participants.draft_hint")}
                        </p>
                      </div>

                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-6">
                          <ParticipantsSection
                            drag={false}
                            showAdmin={false}
                            roles={workOrderParticipantsRole}
                            {...formik}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {work_order?.id &&
                  canAccessProjectPlan &&
                  (work_order?.project_id || project?.id) && (
                    <WorkOrderPlanSection
                      workOrder={work_order}
                      project={project}
                      canEdit={canEditWorkOrderUi}
                    />
                  )}

                {work_order?.id && (
                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6">
                          <DocumentCenter
                            subtitle="Last opp bilder og dokumenter"
                            module="work_order_description"
                            moduleParam={work_order?.id}
                          />
                        </div>
                        <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                      </div>
                    </div>
                  </div>
                )}

                {work_order?.id && (
                  <div className="mt-5 lg:grid lg:grid-cols-2 lg:gap-6">
                    <TimeRegistrationSection
                      className="hidden lg:block"
                      module="work_order"
                      moduleId={work_order.id}
                      project={project ?? work_order?.project}
                      workOrder={{
                        ...work_order,
                        project_id:
                          formik.values.project_id ?? work_order.project_id,
                        project: project ?? work_order?.project,
                        project_activity_id:
                          formik.values.project_activity_id ??
                          work_order.project_activity_id,
                        project_activity:
                          formik.values.project_activity ??
                          work_order.project_activity,
                      }}
                      onRefresh={() => {
                        if (work_order?.id) {
                          const filters = [
                            { name: "WORK_ORDER", value: work_order.id },
                          ];
                          dispatch(
                            getHoursAsync({ ...hoursController, filters })
                          );
                        }
                      }}
                      title="Timeregistrering"
                      description="Registrer timer for denne arbeidsordren"
                      openRegistrationDialogSignal={
                        openRegistrationDialogSignal
                      }
                    />

                    <div
                      role="button"
                      tabIndex={0}
                      disabled={!permissions.canItemConsumption}
                      className={`hidden lg:block shadow sm:overflow-hidden sm:rounded-md cursor-pointer transition-colors duration-200 hover:bg-gray-50 ${
                        !permissions.canItemConsumption
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      onClick={
                        permissions.canItemConsumption
                          ? () =>
                              navigate(
                                `/dashboard/work_orders/${work_order?.id}/item-consumption`
                              )
                          : undefined
                      }
                      onKeyDown={(e) => {
                        if (!permissions.canItemConsumption) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(
                            `/dashboard/work_orders/${work_order?.id}/item-consumption`
                          );
                        }
                      }}
                    >
                      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0">
                            <CubeIcon className="h-12 w-12 text-teal-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              Vareforbruk
                            </h3>
                            <div className="mt-2">
                              <p className="text-sm text-gray-500">
                                Registrer forbruk av materialer og varer
                              </p>
                              <div className="mt-2 flex items-center gap-2">
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                  <PlusIcon className="h-3 w-3 mr-1" />
                                  {work_order?.items?.length || 0} varer
                                  registrert
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {qualityChecklistsModule && work_order?.id ? (
                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {t("work_orders.checklist.section_title")}
                      </h3>
                      <WorkOrderChecklistPanel
                        workOrder={work_order}
                        onWorkOrderUpdated={(patch) =>
                          setWorkOrder((w) =>
                            w ? { ...w, ...patch } : w
                          )
                        }
                        canEdit={canMutateWorkOrderFields}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="mt-5" />

                {canMutateWorkOrderFields ? (
                  drawerMode ? (
                    <div className="wo-redesign">
                      <div className="wo-drawer-foot">
                        {onCancelEdit ? (
                          <button
                            type="button"
                            className="wbtn"
                            onClick={onCancelEdit}
                          >
                            {t("Cancel")}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="wbtn primary"
                          style={{ flex: 1, justifyContent: "center" }}
                          disabled={
                            formik.isSubmitting ||
                            customerLockBlocksSubmit(formik.values, {
                              originalCustomerId: formik.values?.id
                                ? formik.initialValues?.customer_id
                                : null,
                            })
                          }
                          onClick={formik.submitForm}
                        >
                          {formik.isSubmitting ? t("Saving") : t("Save")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <FormikSaveBar
                      {...formik}
                      dockClassName="bottom-0 z-50 pb-2 sm:pb-5 max-lg:bottom-[4.75rem]"
                    />
                  )
                ) : null}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}
